<template>
  <div :id="componentAttrIdAutosuggest" v-bind="$attrs">
    <slot name="before-input" />
    <div
      role="combobox"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="listbox"
      :aria-owns="`${componentAttrIdAutosuggest}-${componentAttrPrefix}__results`"
    >
      <input
        :type="internalInputProps.type"
        :value="internalValue"
        :autocomplete="internalInputProps.autocomplete"
        :class="[
          isOpen ? `${componentAttrPrefix}__input--open` : '',
          internalInputProps['class'],
        ]"
        v-bind="internalInputProps"
        aria-autocomplete="list"
        :aria-activedescendant="
          isOpen && selectedItem !== null
            ? `${componentAttrPrefix}__results-item--${selectedItem.index}`
            : ''
        "
        :aria-controls="`${componentAttrIdAutosuggest}-${componentAttrPrefix}__results`"
        @input="inputHandler"
        @keydown="handleKeyStroke"
        @focus="$emit('focus')"
        v-on="listeners"
      />
    </div>
    <slot name="after-input" />
    <div
      :id="`${componentAttrIdAutosuggest}-${componentAttrPrefix}__results`"
      :class="_componentAttrClassAutosuggestResultsContainer"
    >
      <div
        v-if="isOpen"
        :class="_componentAttrClassAutosuggestResults"
        :aria-labelledby="componentAttrIdAutosuggest"
      >
        <slot name="before-suggestions" />
        <component
          :is="cs.type"
          v-for="(cs, key) in computedSections"
          :ref="(el) => refSet(el, getSectionRef(`${cs.name}${key}`))"
          :key="getSectionRef(`${cs.name}${key}`)"
          :current-item="selectedItem"
          :normalize-item-function="normalizeItem"
          :render-suggestion="renderSuggestion"
          :section="cs"
          :component-attr-prefix="componentAttrPrefix"
          :component-attr-id-autosuggest="componentAttrIdAutosuggest"
          @update-current-item="updateCurrentItem"
        >
          <template
            v-if="$slots[`before-section-${cs.name || cs.label}`]"
            #[`before-section-${cs.name||cs.label}`]="{ section, className }"
            :key="index"
          >
            <slot
              :name="`before-section-${cs.name || cs.label}`"
              :section="section"
              :class-name="className"
            />
          </template>

          <template #default="{ suggestion, _key }">
            <slot :suggestion="suggestion" :index="_key">
              {{ suggestion.item }}
            </slot>
          </template>

          <template
            v-if="$slots[`after-section-${cs.name || cs.label}`]"
            #[`after-section-${cs.name||cs.label}`]="{ section }"
          >
            <slot
              :name="`after-section-${cs.name || cs.label}`"
              :section="section"
            />
          </template>

          <template v-if="$slots['after-section']" #after-section="{ section }">
            <slot name="after-section" :section="section" />
          </template>
        </component>
        <slot name="after-suggestions" />
      </div>
      <slot name="after-suggestions-container" />
    </div>
  </div>
</template>

<script setup>
import { isEqual } from 'lodash-es'
import {
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
  useAttrs,
  watch,
} from 'vue'
import DefaultSection from '~/apps/search-wizard/campsites-search/location/components/DefaultSection'

const $refs = ref([])
const refSet = (el, key) => {
  $refs.value[key] = el
  return key
}

const attrs = useAttrs()
const emit = defineEmits([
  'update:modelValue',
  'item-changed',
  'opened',
  'closed',
  'focus',
])

const defaultSectionConfig = {
  name: 'default',
  type: DefaultSection,
}

//
// Props
//
const props = defineProps({
  modelValue: {
    type: String,
    default: null,
  },
  /** v-binds to the <input /> tag for fine-grain control */
  inputProps: {
    type: Object,
    required: true,
  },
  /** limits the number of suggestions for all sections */
  limit: {
    type: Number,
    required: false,
    default: Infinity,
  },
  suggestions: {
    type: Array,
    required: true,
  },
  renderSuggestion: {
    type: Function,
    required: false,
    default: null,
  },
  getSuggestionValue: {
    type: Function,
    required: false,
    default: (suggestion) => {
      const item = suggestion.item
      if (typeof item === 'object' && item.name) {
        return item.name
      } else {
        return item
      }
    },
  },
  shouldRenderSuggestions: {
    type: Function,
    required: false,
    default: (totalResults, loading) => {
      return totalResults > 0 && !loading
    },
  },
  sectionConfigs: {
    type: Object,
    required: false,
    default: () => {
      return {
        default: {
          onSelected: null,
        },
      }
    },
  },
  componentAttrIdAutosuggest: {
    type: String,
    required: false,
    default: 'autosuggest',
  },
  componentAttrClassAutosuggestResultsContainer: {
    type: String,
    required: false,
    default: null, // `${componentAttrPrefix}__results-container`
  },
  componentAttrClassAutosuggestResults: {
    type: String,
    required: false,
    default: null, // `${componentAttrPrefix}__results`
  },
  componentAttrPrefix: {
    type: String,
    required: false,
    default: 'autosuggest',
  },
  currentItem: {
    type: Object,
    default: null,
  },
})

const {
  modelValue,
  suggestions,
  componentAttrIdAutosuggest,
  componentAttrPrefix,
  inputProps,
  renderSuggestion,
  componentAttrClassAutosuggestResults,
  componentAttrClassAutosuggestResultsContainer,
  sectionConfigs,
  shouldRenderSuggestions,
  getSuggestionValue,
} = toRefs(props)

//
// Data
//
const internalValue = ref(null)
const searchInputOriginal = ref(null)
const selectedItem = ref(null)
// TODO use event states instead of generic "loading" variable
const loading =
  ref(
    false,
  ) /** Helps with making sure the dropdown doesn't stay open after certain actions */
const didSelectFromOptions = ref(false)
const didSelectWithKeyboard = ref(false)
const defaultInputProps = {
  type: 'text',
  autocomplete: 'off',
  autocorrect: 'off',
  spellcheck: false,
}
/** @type Number */
const clientXMouseDownInitial = ref(null)

//
// Computed
//
const internalInputProps = computed(() => {
  return {
    ...defaultInputProps,
    ...inputProps.value,
  }
})

const listeners = computed(() => {
  return {
    input: (e) => {
      // Don't do anything native here, since we have inputHandler
    },
    /**
     * Wrap native click handler to allow for added behavior
     */
    click: () => {
      loading.value = false
      attrs.click && attrs.click(selectedItem.value)
      // I don't think this is needed anymore
      // void nextTick(() => {
      //   ensureItemVisible(selectedItem.value, selectedItem.value.index)
      // })
    },
    selected: (didSelectFromOptions, selectingDefault) => {
      /**
       * Determine which onSelected to fire. This can be either from inside
       * a section's object, from the `@selected` event
       */
      if (
        selectedItem.value &&
        sectionConfigs.value[selectedItem.value.name] &&
        sectionConfigs.value[selectedItem.value.name].onSelected
      ) {
        sectionConfigs.value[selectedItem.value.name].onSelected(
          selectedItem.value,
          searchInputOriginal.value,
          selectingDefault,
        )
      } else if (sectionConfigs.value.default.onSelected) {
        sectionConfigs.value.default.onSelected(
          null,
          searchInputOriginal.value,
          selectingDefault,
        )
      } else if (attrs.onSelected) {
        attrs.onSelected(
          selectedItem.value,
          selectedItem.value.index,
          selectingDefault,
        )
      }
      setChangeItem(null)
    },
  }
})

/**
 * Capture mousedown position so we can use it to detect if the scrollbar
 * was clicked
 * @param {MouseEvent} e
 */
const onDocumentMouseDown = (e) => {
  const rect = e.target.getBoundingClientRect
    ? e.target.getBoundingClientRect()
    : 0
  clientXMouseDownInitial.value = e.clientX - rect.left
}

/**
 * 'mouseup' event handler
 * @param {MouseEvent} e
 */
const onDocumentMouseUp = (e) => {
  /** Do not re-render list on input click  */
  const isChild = document.contains(e.target)

  /* Clicks outside of dropdown */
  if (!isChild) {
    loading.value = true
    selectedItem.value = null
    return
  }

  if (
    e.target.tagName === 'INPUT' ||
    clickedOnScrollbar(e, clientXMouseDownInitial.value)
  ) {
    return
  }

  /** Selects an item in the dropdown */
  loading.value = true
  didSelectFromOptions.value = true
  setChangeItem(selectedItem.value, true)
  listeners.value.selected(true)
  emit('blur')
}

const addDocumentListeners = () => {
  document.addEventListener('mouseup', onDocumentMouseUp)
  document.addEventListener('mousedown', onDocumentMouseDown)
}

const removeDocumentListeners = () => {
  if (document) {
    document.removeEventListener('mouseup', onDocumentMouseUp)
    document.removeEventListener('mousedown', onDocumentMouseDown)
  }
}

/**
 * @returns {Boolean}
 */
const isOpen = computed(() => {
  const isOpenValue = shouldRenderSuggestions.value(
    totalResults.value,
    loading.value,
  )
  if (isOpenValue && process.client) {
    addDocumentListeners()
  } else if (process.client) {
    removeDocumentListeners()
  }

  return isOpenValue
})

/**
 * Normalize suggestions into sections based on defaults and section
 * configs.
 * @returns {Array<ResultSection>}
 */
const computedSections = computed(() => {
  let tmpSize = 0

  return suggestions.value.map((section) => {
    if (!section.data) {
      return undefined
    }

    const name = section.name ? section.name : defaultSectionConfig.name
    let _limit
    let _label
    let _type
    let _ulClass
    let _liClass = null

    if (sectionConfigs.value[name]) {
      _limit = sectionConfigs.value[name].limit
      _type = sectionConfigs.value[name].type
      _label = sectionConfigs.value[name].label
      _ulClass = sectionConfigs.value[name].ulClass
      _liClass = sectionConfigs.value[name].liClass
    }

    /** Set defaults for section configs. */
    _type = _type || defaultSectionConfig.type

    _limit = _limit || props.limit
    _limit = _limit || Infinity
    _limit = section.data.length < _limit ? section.data.length : _limit
    _label = _label || section.label

    const computedSection = {
      name,
      label: _label,
      type: _type,
      limit: _limit,
      data: section.data,
      start_index: tmpSize,
      end_index: tmpSize + _limit - 1,
      ulClass: _ulClass,
      liClass: _liClass,
    }

    tmpSize += _limit

    return computedSection
  })
})

/**
 * Calculate number of results in each section.
 * @returns {Number}
 */
const totalResults = computed(() => {
  return computedSections.value.reduce((acc, section) => {
    // For each section, make sure we calculate the size
    // based on how many are rendered, which maxes out at
    // the limit but can be less than the limit.
    if (!section) {
      return acc
    }
    const { limit, data } = section
    return acc + (data.length >= limit ? limit : data.length)
  }, 0)
})

const _componentAttrClassAutosuggestResultsContainer = computed(() => {
  return (
    componentAttrClassAutosuggestResultsContainer.value ||
    `${componentAttrPrefix.value}__results-container`
  )
})

const _componentAttrClassAutosuggestResults = computed(() => {
  return (
    componentAttrClassAutosuggestResults.value ||
    `${componentAttrPrefix.value}__results`
  )
})

//
// WATCH
//
watch(modelValue, (newValue) => {
  internalValue.value = newValue
})

/**
 * Emits opened/closed events
 * @returns {Boolean}
 */
watch(isOpen, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    emit(newValue ? 'opened' : 'closed')
  }
})

//
// METHODS
//

/**
 * handler for @input <input /> events to support v-model behavior.
 * @param {InputEvent} e
 */
const inputHandler = (e) => {
  const newValue = e.target.value
  emit('update:modelValue', newValue)
  internalValue.value = newValue
  if (!didSelectFromOptions.value) {
    searchInputOriginal.value = newValue
    selectedItem.value = null
  }
}

/**
 * Helper for making sure the sectionRef getter is consistent
 * @returns {String}
 */
const getSectionRef = (i) => {
  return 'computed_section_' + i
}

/**
 * Helper for getting a suggestion item by index.
 * @returns {ResultItem}
 */
const getItemByIndex = (index) => {
  let obj = false
  if (index === null) {
    return obj
  }
  for (let i = 0; i < computedSections.value.length; i++) {
    if (
      index >= computedSections.value[i].start_index &&
      index <= computedSections.value[i].end_index
    ) {
      const trueIndex = index - computedSections.value[i].start_index
      const sectionName = computedSections.value[i].name
      const childSection =
        $refs.value[getSectionRef(`${sectionName}${i}`)] || false

      if (childSection) {
        obj = normalizeItem(
          computedSections.value[i].name,
          computedSections.value[i].type,
          childSection.section.label,
          childSection.section.liClass,
          childSection.getItemByIndex(trueIndex),
        )
        break
      }
    }
  }

  return obj
}

/**
 * Handler for 'keydown' event. Does a number of things, including making
 * sure to ignore keycodes, ensure items are visible and also that the input
 * value is updated/reset according to where the user has keyed to.
 * @param {MouseEvent} e
 * @returns {void}
 */
const handleKeyStroke = (e) => {
  const { keyCode } = e
  const ignoredKeyCodes = [
    16, // Shift
    9, // Tab
    17, // ctrl
    18, // alt/option
    91, // OS Key
    93, // Right OS Key
  ]
  if (ignoredKeyCodes.includes(keyCode)) {
    return
  }
  loading.value = false
  didSelectFromOptions.value = false
  if (isOpen.value) {
    const allItems = computedSections.value.flatMap((section) =>
      section.data.slice(0, section.limit),
    )
    const currentIndex = selectedItem.value
      ? allItems.findIndex((item) => isEqual(item, selectedItem.value.item))
      : -1
    const direction = keyCode === 40 ? 1 : -1
    const newIndex = currentIndex + direction
    switch (keyCode) {
      case 40: // ArrowDown
      case 38: // ArrowUp
        e.preventDefault()
        if (newIndex >= 0 && newIndex < allItems.length) {
          const newItem = getItemByIndex(newIndex)
          updateCurrentItem(newItem)
        } else if (newIndex < 0) {
          // TODO maybe reset to original user input when proper text searching is enabled
        }
        break

      case 13: // Enter
        if (selectedItem.value) {
          e.preventDefault()
          setChangeItem(selectedItem.value, true)
        }
        didSelectFromOptions.value = true
        didSelectWithKeyboard.value = true
        loading.value = true
        listeners.value.selected(didSelectFromOptions.value, false)
        break

      case 27: // Escape
        /**
         * For 'search' input type, make sure the browser doesn't clear the
         * input when Escape is pressed.
         */
        loading.value = true
        selectedItem.value = null
        internalValue.value = searchInputOriginal.value
        emit('update:modelValue', searchInputOriginal.value)
        e.preventDefault()
        break
    }
  }
}

/**
 * Wrapper around currentItem setter to emit events and ensure to update the
 * searchInputOriginal when a user selects an option.
 * @param {ResultItem} item
 * @param {Boolean} overrideOriginalInput determine if the 'saved' original
 *   input should be updated. When a user selects an option, this will be
 *   updated, but if a user keys into the <input/> then the input will be
 *   reset to the searchInputOriginal.
 * @return {void}
 */
const setChangeItem = (item, overrideOriginalInput = false) => {
  if (selectedItem.value === null || !item) {
    selectedItem.value = null
    emit('item-changed', null, null)
  } else if (item) {
    selectedItem.value = item
    const v = getSuggestionValue.value(item)
    internalValue.value = v
    if (overrideOriginalInput) {
      searchInputOriginal.value = v
    }
    emit('item-changed', item, selectedItem.value)
  }
}

/**
 * Function to standardize suggestion item object picked from sections
 * @returns {ResultItem}
 */
const normalizeItem = (name, type, label, className, item) => {
  return {
    name,
    type,
    label,
    liClass: item.liClass || className,
    item,
  }
}

/**
 * Adjust the scroll position to the item in the suggestions overflow
 * @param {ResultItem} item - suggestion item
 * @param {Number} index - item index
 * @param {String} selector - selector of item that is overflowed
 */
const ensureItemVisible = (item, index, selector) => {
  const resultsScrollElement = document.querySelector(
    selector || `.${_componentAttrClassAutosuggestResults.value}`,
  )

  if (!resultsScrollElement) {
    return
  }

  const itemElement = resultsScrollElement.querySelector(
    `#${componentAttrPrefix.value}__results-item--${index}`,
  )
  if (!itemElement) {
    return
  }

  const resultsScrollWindowHeight = resultsScrollElement.clientHeight
  const resultsScrollScrollTop = resultsScrollElement.scrollTop

  const itemHeight = itemElement.clientHeight
  const currentItemScrollOffset = itemElement.offsetTop

  if (
    itemHeight + currentItemScrollOffset >=
    resultsScrollScrollTop + resultsScrollWindowHeight
  ) {
    /** Current item goes below visible scroll window */
    resultsScrollElement.scrollTop =
      itemHeight + currentItemScrollOffset - resultsScrollWindowHeight
  } else if (
    currentItemScrollOffset < resultsScrollScrollTop &&
    resultsScrollScrollTop > 0
  ) {
    /** Current item goes above visible scroll window */
    resultsScrollElement.scrollTop = currentItemScrollOffset
  }
}

/**
 * @param {Number} index
 */
const updateCurrentItem = (item) => {
  selectedItem.value = item
  if (item) {
    setChangeItem(item)
    internalValue.value = searchInputOriginal.value
  }
}

/**
 * Helper to detect if the user clicked on the scrollbar
 * @param {MouseEvent} e
 * @param {Number} mouseX - horizontal position of the mouse relative to
 *   results e.g. an offset of clientX
 */
const clickedOnScrollbar = (e, mouseX) => {
  const results = document.querySelector(
    `.${_componentAttrClassAutosuggestResults.value}`,
  )

  const mouseIsInsideScrollbar =
    results &&
    results.clientWidth <= mouseX + 17 &&
    mouseX + 17 <= results.clientWidth + 34
  return (
    (e.target.tagName === 'DIV' && results && mouseIsInsideScrollbar) || false
  )
}

onBeforeMount(() => {
  loading.value = true
})

onMounted(() => {
  internalValue.value = modelValue.value
  // addDocumentListeners()
})

onUnmounted(() => {
  if (removeDocumentListeners) {
    removeDocumentListeners()
  }
})
</script>
